<!-- ライブアーカイブ視聴ページ -->
<script setup lang="ts">
  import { useLiveArchiveStore } from "~/stores/LiveArchiveStore";
  import { useVideoStateStore } from "~/stores/VideoStateStore";
  import { useLoginStore } from "~/stores/LoginStore";
  import { useContentsStore } from "~/stores/ContentsStore";
  import { useHistoryStore } from "~/stores/HistoryStore";
  const historyStore = useHistoryStore();

  // ビデオ周りは複雑なレイアウトなのでステート管理にpiniaを使用する
  const videoStateStore = useVideoStateStore();
  videoStateStore.reset();

  const liveId = useRoute().params.contentId as string;

  const loginStore = useLoginStore();
  const contentsStore = useContentsStore();

  const liveStore = useLiveArchiveStore();
  liveStore.fetchOne(liveId);

  const hasError = ref<boolean>(false);

  const onClickToPlanTop = () => {
    return historyStore.to({
      path: "/plan/" + liveStore.liveArchive?.plan?.id,
    });
  };

  watch(
    () => {
      return liveStore.liveArchive?.liveStartDateTime;
    },
    () => {
      if (liveStore.liveArchive?.liveStartDateTime) {
        const startDate = new Date(liveStore.liveArchive.liveStartDateTime);
        videoStateStore.setStartDateTime(startDate);
      }
    },
  );
  // ラジオモードかどうか
  const radioMode = computed<boolean>(() => {
    if (!liveStore.liveArchive) {
      return false;
    }
    if (liveStore.liveArchive?.streamingVideoSettingList?.length > 0 && videoStateStore.currentDateTime) {
      const currentDateTime = videoStateStore.currentDateTime.getTime();
      const streamingVideoSetting = liveStore.liveArchive?.streamingVideoSettingList.findLast(item => {
        const itemDateTime = new Date(item.dateTime).getTime();
        return itemDateTime <= currentDateTime;
      });
      if (streamingVideoSetting) {
        return !streamingVideoSetting.videoStreaming;
      }
    }
    return !liveStore.liveArchive?.videoStreamingFlag;
  });

  // meta/ogp
  const headTitle = ref();
  const headDescription = ref();
  const ogpUrl = ref();
  const ogpImage = ref();
  const setMeta = () => {
    if (liveStore.liveArchive) {
      headTitle.value = liveStore.liveArchive.contentsTitle + ` | Fincs`;
      headDescription.value = liveStore.liveArchive.description;
      ogpUrl.value = `https://fincs.jp/plan/${liveStore.liveArchive?.plan?.id}/livearchive/${liveStore.liveArchive.id}/`;
      ogpImage.value = liveStore.liveArchive.thumbnailImage;
    }
  };

  watch(
    () => {
      return liveStore.liveArchive;
    },
    () => {
      setMeta();
    },
    { immediate: true },
  );

  // 閲覧フラグを立てる。閲覧回数にも使われているので毎回呼ぶ
  watch(
    () => {
      return liveStore.liveArchive;
    },
    (newValue, oldValue) => {
      if (newValue && newValue !== oldValue) {
        if (!loginStore.isLogin()) {
          return;
        }
        try {
          contentsStore.postBrowse(liveStore.liveArchive!.id.toString(), loginStore.loginInfo.userId);
        } catch (error: any) {
          console.log(error);
        }
      }
    },
  );
  useHead({
    title: headTitle,
    meta: [
      { name: "description", content: headDescription },
      { property: "og:url", content: ogpUrl },
      { property: "og:title", content: headTitle },
      { property: "og:description", content: headDescription },
      { property: "og:image", content: ogpImage },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "ja_JP" },
      { property: "og:site_name", content: "Fincs" },
    ],
  });
</script>

<template>
  <div>
    <NuxtLayout>
      <OrganismsMainLayout :enable-landscape-header="true">
        <OrganismsLivePageLayout :live-info="liveStore.liveArchive" :loading="liveStore.loading" type="archive">
          <template #video>
            <OrganismsVideoPlayer
              v-model:has-error="hasError"
              :contents-id="liveId"
              :contents-url="liveStore.liveArchive?.contentsUrl"
              :contents-sub-url="liveStore.liveArchive?.contentsSubUrl"
              :thumbnail-image="liveStore.liveArchive?.thumbnailImage"
              :autoplay="false"
              :muted="false"
              :embed="true"
              :duration="liveStore.liveArchive?.moviePlayTimeSec"
              :is-complete="liveStore.liveArchive?.isComplete"
            >
              <AtomsRadioModePanel v-if="radioMode" :src="liveStore.liveArchive?.thumbnailImage"> </AtomsRadioModePanel>
            </OrganismsVideoPlayer>
            <MoleculesContentsDialog
              v-if="hasError"
              message="エラーが発生しました"
              button-title="講座トップへ"
              @click="onClickToPlanTop"
            />
          </template>
        </OrganismsLivePageLayout>
      </OrganismsMainLayout>
    </NuxtLayout>
  </div>
</template>
